import type { ClassValue } from '@vs-shop/utils';
import type { ComponentType } from 'react';

import * as LucideIcons from 'lucide-react';

/**
 * Converts kebab-case icon name to PascalCase for Lucide Icons
 * @example kebabToPascal('circle-check-big') => 'CircleCheckBig'
 * @example kebabToPascal('airplay') => 'Airplay'
 */
export function kebabToPascal(str: string): string {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
}

/**
 * Safely retrieves a Lucide icon component by kebab-case name
 * Returns null if the icon doesn't exist
 * @example getLucideIcon('circle-check-big') => CircleCheckBig component or null
 * @example getLucideIcon('airplay') => Airplay component or null
 */
/**
 * A lucide icon's OWN prop surface, not a `className` this repo declares.
 * `ClassValue` here would make every lucide icon unassignable — a component
 * taking a narrower prop cannot stand where a wider one is expected — and the
 * cast below would then launder a `ClassArray` into a component that would
 * render it as `[object Object]`. What this repo authors takes `ClassValue`;
 * what it merely renders is typed as that component types itself.
 */
export function getLucideIconFromKebabString(
  iconName: string
): ComponentType<{ className?: string }> | null {
  const pascalName = kebabToPascal(iconName);

  const iconsRecord = LucideIcons as Record<string, unknown>;

  // Use Object.prototype.hasOwnProperty to safely check and access
  if (!Object.prototype.hasOwnProperty.call(iconsRecord, pascalName)) {
    return null;
  }

  const IconComponent = iconsRecord[pascalName];

  // Validate that it's actually a component (not a helper function or type)
  if (typeof IconComponent === 'function' || typeof IconComponent === 'object') {
    return IconComponent as ComponentType<{ className?: string }>;
  }

  return null;
}
